import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Code, Palette, Zap, Shield, TrendingUp } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState } from "react";
import ContactFormModal from "@/components/ContactFormModal";

export default function Websites() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const features = [
    {
      icon: Code,
      title: "Custom Development",
      description: "Built from scratch to match your exact needs, not forced into a template"
    },
    {
      icon: Palette,
      title: "Unique Design",
      description: "Stand out with a design that reflects your brand identity"
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Optimized for speed with PageSpeed scores of 90+"
    },
    {
      icon: Shield,
      title: "Secure & Reliable",
      description: "Enterprise-grade security and 99.9% uptime guarantee"
    },
    {
      icon: TrendingUp,
      title: "SEO Optimized",
      description: "Built with search engines in mind from day one"
    },
  ];

  const process = [
    { step: "1", title: "Discovery", description: "We learn about your business, goals, and target audience" },
    { step: "2", title: "Design", description: "Create mockups and prototypes for your approval" },
    { step: "3", title: "Development", description: "Build your site with clean, modern code" },
    { step: "4", title: "Launch", description: "Deploy to production and monitor performance" },
    { step: "5", title: "Support", description: "Ongoing maintenance and updates as needed" },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-background" />
          
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Custom <span className="text-gradient">Websites</span> That Convert
            </motion.h1>
            
            <motion.p
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              No templates. No limitations. Just a website built specifically for your business goals.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Button
                size="lg"
                onClick={() => setIsContactOpen(true)}
                className="bg-gradient-to-r from-primary to-secondary text-white"
              >
                Start Your Project
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 px-4">
          <div className="container max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Why Choose Custom Development?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="p-6 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <feature.icon className="w-12 h-12 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Timeline */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Our Process
            </h2>
            
            <div className="space-y-8">
              {process.map((item, index) => (
                <motion.div
                  key={item.step}
                  className="flex gap-6 items-start"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Build Something Amazing?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's discuss your project and create a website that drives real results.
            </p>
            <Button
              size="lg"
              onClick={() => setIsContactOpen(true)}
              className="bg-gradient-to-r from-primary to-secondary text-white"
            >
              Get Started Today
            </Button>
          </div>
        </section>
      </div>
      <Footer />
      <ContactFormModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </>
  );
}
